// 조회조건 저장·복원 — 순수 변환 (화면 배선 `index.tsx` 에서 소비, 테스트 대상)
//
// 마지막 조회 조건을 메뉴 세션(sessionStorage)·URL 쿼리에 남겨 다른 메뉴를 다녀와도 유지한다(MOMS 공통 UX).
// 저장 경로는 `MenuStorage.menuOnSession.setSearch` → `Utils.qsStringify`, 복원 경로는
// `useParsedQuery`(URL 쿼리) 다. 세션은 탭바·LNB 가 링크를 조립할 때 쓰는 운반체일 뿐, 복원의 실제
// 출처는 URL 이다(erpInbound/index.tsx 준용 — 공통 결정서 D16, D3 의 전용 훅 지시는 폐기됐다).
//
// 저장 대상은 조회 필터 두 칸(등록센터·지정일)뿐이다. 반송번호(스캔 입력)는 D3 이 유지 대상에서
// 명시적으로 제외했다 — 다음 작업 건과 섞이면 안 된다. 페이지 번호·목록 검색어는 이 화면에 없다
// (서버 페이징도 목록 검색도 없는 화면 — 스캔 결과가 화면 메모리에 최신순으로 쌓일 뿐이다).
//
// 복원은 폼 버스 슬롯(`wiring.form.rgr08ScanBar`)이 한다 — 생성본이 든 값 중 **아직 비어 있는 칸에만**
// 복원값을 얹는다(컴포넌트 모드, 문서 51). 값이 바뀌면 그 슬롯이 배선으로 끌어올려 여기 변환을 다시 태운다.
import dayjs from 'dayjs'
import { ReceiptHistoryFilter } from '@/services/return/returnReceiptReceive/useReturnReceiptReceiveQueries'
import { CENTER_OPTIONS, DEFAULT_CENTER_NM } from './constants'

const DATE_FORMAT = 'YYYYMMDD'

/** URL·세션에 남는 형태. 값이 있는 키만 싣는다. */
export interface Rgr08SearchQuery {
  centerNm?: string
  assignDateFrom?: string
  assignDateTo?: string
}

/** 폼 값 모양 — `ScanFilter` 가 심는 필드 이름과 같다. */
export interface Rgr08FilterFormValues {
  centerNm: string
  assignDateRange: [dayjs.Dayjs, dayjs.Dayjs]
}

/** 폼 값이 아니어도(복원 경로의 원시 값) 안전하게 받는다. `Dayjs` 는 쿼리스트링으로 직렬화되지 않아 압축형으로 눕힌다. */
const toDateString = (value: unknown): string | undefined => {
  if (!value) return undefined
  const parsed = dayjs(value as dayjs.ConfigType)
  return parsed.isValid() ? parsed.format(DATE_FORMAT) : undefined
}

/** 폼 값(등록센터·지정일) → 저장 형태. */
export const toSearchQuery = (values: Rgr08FilterFormValues): Rgr08SearchQuery => {
  const [from, to] = values.assignDateRange ?? []
  const fromStr = toDateString(from)
  const toStr = toDateString(to)
  return {
    ...(values.centerNm ? { centerNm: values.centerNm } : {}),
    ...(fromStr ? { assignDateFrom: fromStr } : {}),
    ...(toStr ? { assignDateTo: toStr } : {}),
  }
}

/**
 * 폼 값 → 이력 조회 조건. (LOGISPRD-12610)
 *
 * 지정일(수령) 범위를 마감일자(`CLS_DATE`) 범위로 옮긴다. BE 조회 계약(`ReturnReceiptController`)에
 * ASSIGN_DATE 필터가 없어서다 — 스캔은 마감일자를 그날로 적으므로(moms-be `ReturnReceiptReceiveService`)
 * 지정일을 기본값(오늘)으로 두고 스캔하는 평시 동선에서는 두 값이 같다.
 *
 * 두 값이 어긋난 행에서는 양방향으로 틀어진다. 지정일을 다른 날로 바꿔 스캔한 행은 이 조회에 걸리지
 * 않고(누락), 반대로 마감일자만 범위에 드는 행은 지정일이 달라도 결과에 섞인다(혼입). 조회 조건이
 * 등록센터와 마감일자 범위뿐이라 지정일로는 아무것도 거르지 못해서다. BE 에 지정일 필터가 생기면
 * 그때 바꾼다 — 응답을 화면에서 지정일로 다시 거르는 방식은 쓰지 않는다. 중복스캔 판정이 기존 행의
 * ASSIGN_DATE 를 그대로 복사하므로(moms-be `ReturnReceiptReceiveService`) 오늘 스캔한 중복 건이
 * 도리어 사라진다.
 *
 * 값이 하나라도 비면 `null` 을 돌려 조회를 켜지 않는다. 등록센터 없이 부르면 전 센터 전건이 온다.
 */
export const toHistoryFilter = (values: Partial<Rgr08FilterFormValues>): ReceiptHistoryFilter | null => {
  const [from, to] = values.assignDateRange ?? []
  const clsDateFrom = toDateString(from)
  const clsDateTo = toDateString(to)
  if (!values.centerNm || !clsDateFrom || !clsDateTo) return null
  return { centerNm: values.centerNm, clsDateFrom, clsDateTo }
}

const VALID_CENTERS = new Set<string>(CENTER_OPTIONS.map((option) => option.value))

/** 압축형(`yyyyMMdd`) 문자열 → `Dayjs`. 형식이 어긋나면 버린다(조작된 URL 방어). */
const asRestoredDate = (value: unknown) => {
  if (typeof value !== 'string' || !/^\d{8}$/.test(value)) return null
  const parsed = dayjs(`${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6)}`)
  return parsed.isValid() ? parsed : null
}

/**
 * 저장된 쿼리 → 폼 초기값. 이 화면에는 "복원 대상이 없으면 빈 표로 연다" 같은 개념이 없다 —
 * 필터가 곧 스캔 요청 파라미터라 항상 유효한 값(기본값 신여주1·오늘)으로 열어야 한다.
 *
 * 등록센터가 알 수 없는 값(조작된 URL 등)이면 기본값으로 되돌린다.
 */
export const fromSearchQuery = (query: Partial<Rgr08SearchQuery>): Rgr08FilterFormValues => {
  const centerNm = typeof query.centerNm === 'string' && VALID_CENTERS.has(query.centerNm) ? query.centerNm : DEFAULT_CENTER_NM
  const from = asRestoredDate(query.assignDateFrom) ?? dayjs()
  const to = asRestoredDate(query.assignDateTo) ?? dayjs()
  return { centerNm, assignDateRange: [from, to] }
}
